<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import Drawer from 'primevue/drawer';
import Button from 'primevue/button';
import Badge from 'primevue/badge';
import Divider from 'primevue/divider';
import { useToast } from 'primevue/usetoast';
import { useCart } from '@/composables/useCart';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

// Use cart composable
const {
    cartCount,
    cartItems: items,
    loading,
    loadCart,
    updateQuantity: updateQty,
    removeFromCart,
    clearCart,
    loadCartCount
} = useCart();

// Currency formatter composable
const { formatConvertedPrice, getCurrentCurrency, onCurrencyChange } = useCurrencyFormatter();
const currentCurrency = ref(getCurrentCurrency());

// Drawer visibility
const visible = ref(false);

// Cart state for display
const cartItems = ref([]);
const totalLinks = ref(0);
const totalPrice = ref(0);

// Toggle drawer
const toggleDrawer = async () => {
    visible.value = !visible.value;
    if (visible.value) {
        await loadCartData();
    }
};

// Load cart from API
const loadCartData = async () => {
    try {
        const data = await loadCart();
        cartItems.value = data.items;
        totalLinks.value = data.total_links;
        totalPrice.value = data.total_price;
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Failed to load cart'),
            life: 3000
        });
    }
};

// Update item quantity
const updateQuantity = async (siteId, newQuantity) => {
    if (newQuantity < 0 || newQuantity > 50) return;

    try {
        const data = await updateQty(siteId, newQuantity);
        cartItems.value = data.items;
        totalLinks.value = data.total_links;
        totalPrice.value = data.total_price;
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Failed to update quantity'),
            life: 3000
        });
    }
};

// Increment quantity
const incrementQuantity = (item) => {
    const newQuantity = item.quantity + 1;
    if (newQuantity <= 50) {
        updateQuantity(item.id, newQuantity);
    }
};

// Decrement quantity (removes item if quantity becomes 0)
const decrementQuantity = (item) => {
    const newQuantity = item.quantity - 1;
    if (newQuantity <= 0) {
        removeItem(item.id);
    } else {
        updateQuantity(item.id, newQuantity);
    }
};

// Remove item from cart
const removeItem = async (siteId) => {
    try {
        const data = await removeFromCart(siteId);
        cartItems.value = data.items;
        totalLinks.value = data.total_links;
        totalPrice.value = data.total_price;

        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Item removed from cart'),
            life: 3000
        });
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Failed to remove item'),
            life: 3000
        });
    }
};

// Clear entire cart
const clearAllItems = async () => {
    try {
        await clearCart();
        cartItems.value = [];
        totalLinks.value = 0;
        totalPrice.value = 0;

        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Cart cleared successfully'),
            life: 3000
        });
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Failed to clear cart'),
            life: 3000
        });
    }
};

// Proceed to checkout
const checkout = () => {
    visible.value = false;
    router.visit(proxy.route('client.checkout.index'));
};

// Load cart count on mount
onMounted(async () => {
    await loadCartCount();

    // Listen for currency changes - Vue reactivity will update prices automatically
    onCurrencyChange(({ currency, symbol }) => {
        currentCurrency.value = currency;
        // No need to reload - conversion happens in frontend based on currentCurrency
    });

    currentCurrency.value = getCurrentCurrency();

    // Listen for cart updates
    window.addEventListener('cart-item-added', (event) => {
        if (visible.value) {
            loadCartData();
        }
    });

    window.addEventListener('cart-updated', (event) => {
        if (visible.value) {
            loadCartData();
        }
    });
});

// Expose toggle method for parent component
defineExpose({
    toggleDrawer
});
</script>

<template>
    <!-- Cart Button -->
    <div class="relative">
        <Button
            severity="secondary"
            outlined
            class="relative flex items-center justify-center"
            style="width: 40px; height: 40px;"
            @click="toggleDrawer"
        >
            <div>
                <!-- Ícone do carrinho -->
                <i class="pi pi-shopping-cart text-xl"></i>
            </div>
        </Button>

        <!-- Badge de quantidade -->
        <Badge
            v-if="cartCount > 0"
            :value="cartCount"
            severity="danger"
            class="absolute -top-2 -right-2"
            style="transform: scale(0.7);"
        />
    </div>

    <!-- Cart Drawer -->
    <Drawer
        v-model:visible="visible"
        position="right"
        :style="{ width: '450px' }"
        class="cart-drawer"
    >
        <template #header>
            <div class="flex items-center justify-between w-full">
                <span class="font-semibold text-xl">{{ $t('Shopping Cart') }}</span>
                <Button
                    v-if="cartItems.length > 0"
                    icon="pi pi-trash"
                    severity="danger"
                    text
                    rounded
                    size="small"
                    @click="clearAllItems"
                    :disabled="loading"
                    v-tooltip.bottom="$t('Clear cart')"
                    class="ml-2"
                />
            </div>
        </template>
        <div v-if="loading" class="flex items-center justify-center h-64">
            <i class="pi pi-spin pi-spinner text-4xl text-primary-500"></i>
        </div>

        <div v-else-if="cartItems.length === 0" class="flex flex-col items-center justify-center h-64 text-center">
            <i class="pi pi-shopping-cart text-6xl text-surface-300 mb-4"></i>
            <p class="text-surface-600 text-lg mb-2">{{ $t('Your cart is empty') }}</p>
            <p class="text-surface-500 text-sm">{{ $t('Add sites to your cart to get started') }}</p>
        </div>

        <div v-else class="flex flex-col h-full">
            <!-- Cart Items -->
            <div class="flex-1 overflow-y-auto">
                <div
                    v-for="item in cartItems"
                    :key="item.id"
                    class="mb-4 p-4 border border-surface-200 rounded-lg hover:shadow-md transition-shadow"
                >
                    <div class="flex items-start justify-between mb-3">
                        <div class="flex-1">
                            <h4 class="font-semibold text-surface-900 mb-1">{{ item.url }}</h4>
                            <p class="text-sm text-surface-600">
                                {{ formatConvertedPrice(item.converted_price, item.converted_coin) }} {{ $t('per link') }}
                            </p>
                        </div>
                        <Button
                            icon="pi pi-trash"
                            severity="danger"
                            text
                            rounded
                            size="small"
                            @click="removeItem(item.id)"
                            :disabled="loading"
                        />
                    </div>

                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-2">
                            <Button
                                icon="pi pi-minus"
                                severity="secondary"
                                outlined
                                size="small"
                                @click="decrementQuantity(item)"
                                :disabled="loading"
                            />
                            <span class="w-12 text-center font-semibold">{{ item.quantity }}</span>
                            <Button
                                icon="pi pi-plus"
                                severity="secondary"
                                outlined
                                size="small"
                                @click="incrementQuantity(item)"
                                :disabled="loading || item.quantity >= 50"
                            />
                        </div>
                        <div class="text-right">
                            <p class="text-sm text-surface-600">{{ $t('Subtotal') }}</p>
                            <p class="font-semibold text-lg text-primary-600">
                                {{ formatConvertedPrice(item.subtotal, item.converted_coin) }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Cart Summary -->
            <div class="border-t border-surface-200 pt-4 mt-4">
                <div class="space-y-3 mb-4">
                    <div class="flex justify-between items-center">
                        <span class="text-surface-700">{{ $t('Total Links') }}:</span>
                        <span class="font-semibold text-lg">{{ totalLinks }}</span>
                    </div>
                    <Divider class="my-2" />
                    <div class="flex justify-between items-center">
                        <span class="text-xl font-semibold text-surface-900">{{ $t('Total') }}:</span>
                        <span class="text-2xl font-bold text-primary-600">
                            {{ formatConvertedPrice(totalPrice, cartItems[0]?.converted_coin || 'USD') }}
                        </span>
                    </div>
                </div>

                <Button
                    :label="$t('Proceed to Checkout')"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    class="w-full"
                    size="large"
                    @click="checkout"
                    :disabled="loading || cartItems.length === 0"
                />
            </div>
        </div>
    </Drawer>
</template>

<style scoped>
.cart-drawer :deep(.p-drawer-content) {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.cart-drawer :deep(.p-drawer-header) {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}
</style>
